import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../api/supabase';
import { useAuth } from '../../context/AuthContext';
import PrintFooter from '../../components/print/PrintFooter';
import PagedPrintView from '../../components/print/PagedPrintView';
import TypeSearch from '../../components/common/TypeSearch';

export default function SaleQuotationPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const username = user?.email?.split('@')[0] || 'unknown';
  const [quot, setQuot] = useState(null);
  const [terms, setTerms] = useState('');
  const [loading, setLoading] = useState(true);
  const [proForma, setProForma] = useState(null);
  const [showConvert, setShowConvert] = useState(false);
  const [convertClientId, setConvertClientId] = useState('');
  const [convertClientLabel, setConvertClientLabel] = useState('');
  const [converting, setConverting] = useState(false);
  const [convertError, setConvertError] = useState('');

  useEffect(() => {
    Promise.all([
      supabase.from('sale_quotations').select('*, agents:agent_ref_id(tls_code, full_name)').eq('id', id).single(),
      supabase.from('terms_templates').select('content').eq('product_type', 'sale_quotation').single(),
    ]).then(([{ data }, { data: t }]) => {
      setQuot(data);
      setTerms(t?.content || '');
      setLoading(false);
      if (data?.converted_pro_forma_id) {
        supabase.from('quotations').select('quotation_number, converted_invoice_id').eq('id', data.converted_pro_forma_id).single()
          .then(({ data: pf }) => setProForma(pf));
      }
    });
  }, [id]);

  const handleConvertToProForma = async () => {
    if (!convertClientId) { setConvertError('Select a client'); return; }
    setConverting(true); setConvertError('');
    try {
      const { data: currRow } = await supabase.from('currencies').select('id').eq('code', quot.currency_code).single();
      const dd = quot.detail_data || {};
      const firstOption = isDetail ? (dd.options?.length ? dd.options[0] : dd) : null;
      const perPaxRate = isDetail
        ? parseFloat(firstOption?.pax_pricing?.find(p => parseFloat(p.rate) > 0)?.rate || 0)
        : parseFloat(quot.per_pax_rate || 0);
      const numPax = parseInt(quot.min_pax) || 1;
      const totalAmount = perPaxRate * numPax;

      const yy = String(new Date().getFullYear()).slice(-2);
      const prefix = `PRO-${yy}`;
      const { data: existing } = await supabase.from('quotations').select('quotation_number').ilike('quotation_number', `${prefix}-%`);
      const maxSeq = (existing || []).reduce((max, r) => {
        const seq = parseInt((r.quotation_number || '').split('-').pop()) || 0;
        return seq > max ? seq : max;
      }, 0);
      const quotation_number = `${prefix}-${String(maxSeq + 1).padStart(4, '0')}`;

      const { data: pf, error } = await supabase.from('quotations').insert({
        quotation_number,
        client_id: convertClientId,
        agent_ref_id: quot.agent_ref_id || null,
        item_type: 'package',
        num_pax: numPax,
        amount_per_pax: perPaxRate || 0,
        total_amount: totalAmount,
        currency_id: currRow?.id || null,
        details: [quot.title, quot.destination].filter(Boolean).join(' — '),
        valid_until: quot.valid_until || null,
        notes: quot.notes || null,
        op_data: { source_sale_quotation_id: quot.id, source_quotation_number: quot.quotation_number },
        status: 'DRAFT',
        created_by: username,
        branch_id: quot.branch_id || null,
      }).select().single();
      if (error) throw error;

      await supabase.from('sale_quotations').update({ converted_pro_forma_id: pf.id }).eq('id', id);
      navigate(`/quotations/${pf.id}?print=1`);
    } catch (e) {
      setConvertError(e.message || 'Error converting');
      setConverting(false);
    }
  };

  const handlePrint = () => window.print();

  const handleDelete = async () => {
    if (!window.confirm('Delete this quotation?')) return;
    await supabase.from('sale_quotations').delete().eq('id', id);
    navigate('/sale-quotations');
  };

  const handleDuplicate = async () => {
    const yy = String(new Date().getFullYear()).slice(-2);
    const prefix = `QT-${yy}`;
    const { data: existing } = await supabase.from('sale_quotations').select('quotation_number').ilike('quotation_number', `${prefix}-%`);
    const maxSeq = (existing || []).reduce((max, r) => {
      const seq = parseInt((r.quotation_number || '').split('-').pop()) || 0;
      return seq > max ? seq : max;
    }, 0);
    const quotation_number = `${prefix}-${String(maxSeq + 1).padStart(4, '0')}`;
    // Copy everything except identity/audit fields — the duplicate starts as its own
    // editable draft, not linked back to the original or its Pro Forma conversion in any way.
    const { id: _id, quotation_number: _qn, created_at, updated_at, created_by, status, converted_pro_forma_id, agents, ...rest } = quot;
    const { data: dup, error } = await supabase.from('sale_quotations').insert({
      ...rest, quotation_number, status: 'DRAFT', created_by: username,
    }).select().single();
    if (error) { alert(error.message); return; }
    navigate(`/sale-quotations/${dup.id}/edit`);
  };

  if (loading) return <div className="spinner" style={{ padding: 40 }} />;
  if (!quot) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Quotation not found.</div>;

  const today = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const isDetail = quot.quotation_type === 'detail_package';
  const dd = quot.detail_data || {};
  // Multi-option quotations (detail_data.options); legacy single-option quotations saved
  // before this feature keep printing exactly as before, wrapped as one implicit option.
  const options = isDetail
    ? (dd.options?.length ? dd.options : [{ ...dd, inclusions: quot.inclusions, exclusions: quot.exclusions }])
    : [];

  return (
    <>
      <div className="no-print" style={{
        display: 'flex', gap: 10, alignItems: 'center', padding: '12px 24px',
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
        position: 'sticky', top: 0, zIndex: 10,
      }}>
        <button className="btn btn-secondary" onClick={() => navigate('/sale-quotations')}>← Back</button>
        <span style={{ fontFamily: 'monospace', fontSize: 13, color: 'var(--text-muted)' }}>{quot.quotation_number}</span>
        <span className={`badge ${quot.status === 'SENT' ? 'badge-green' : 'badge-gray'}`}>{quot.status}</span>
        {isDetail && <span style={{ fontSize: 11, background: '#fff4ef', color: '#c05020', padding: '2px 8px', borderRadius: 99, fontWeight: 600 }}>Detail Package</span>}
        {quot.agents && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Agent: <strong>{quot.agents.tls_code}</strong></span>}
        <div style={{ flex: 1 }} />
        {proForma ? (
          <button className="btn btn-secondary" onClick={() => navigate(`/quotations/${quot.converted_pro_forma_id}?print=1`)}>
            View Pro Forma ({proForma.quotation_number}){proForma.converted_invoice_id ? ' — Invoiced' : ''}
          </button>
        ) : (
          <button className="btn btn-secondary" onClick={() => setShowConvert(true)}>Convert to Pro Forma</button>
        )}
        <Link to={`/sale-quotations/${id}/edit`} className="btn btn-secondary">Edit</Link>
        <button className="btn btn-secondary" onClick={handleDuplicate}>Duplicate</button>
        <button className="btn btn-secondary" onClick={async () => {
          await supabase.from('sale_quotations').update({ status: quot.status === 'SENT' ? 'DRAFT' : 'SENT' }).eq('id', id);
          setQuot(q => ({ ...q, status: q.status === 'SENT' ? 'DRAFT' : 'SENT' }));
        }}>{quot.status === 'SENT' ? 'Mark Draft' : 'Mark Sent'}</button>
        <button className="btn btn-danger" onClick={handleDelete}>Delete</button>
        <button className="btn btn-primary" onClick={handlePrint}>Print / PDF</button>
      </div>

      {showConvert && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }} className="no-print">
          <div style={{ background: 'white', borderRadius: 10, padding: 28, width: 400, boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>Convert to Pro Forma</div>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              This quotation has no client attached yet — pick who it's being sent to now.
            </div>
            <TypeSearch
              fetchFn={async (q) => {
                const { data } = await supabase.from('clients').select('id, full_name, client_code').eq('is_active', true).or(`full_name.ilike.%${q}%,client_code.ilike.%${q}%`).limit(30);
                return (data || []).map(c => ({ ...c, label: `${c.full_name} (${c.client_code})` }));
              }}
              value={convertClientLabel}
              displayKey="label"
              placeholder="Search client…"
              onSelect={c => { setConvertClientId(c.id); setConvertClientLabel(c.label); }}
              onClear={() => { setConvertClientId(''); setConvertClientLabel(''); }}
            />
            {convertError && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 8 }}>{convertError}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button className="btn btn-primary" onClick={handleConvertToProForma} disabled={converting}>{converting ? 'Converting…' : 'Convert'}</button>
              <button className="btn btn-secondary" onClick={() => { setShowConvert(false); setConvertError(''); }}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      <PagedPrintView docKey={id}>
        {/* PagedJS only starts repeating a "running" element (like the footer) on
            pages AFTER it first encounters that element in the source content — so
            it must come first here, even though position:running() means it never
            renders in place. */}
        <PrintFooter />

        {/* Letterhead */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #c8a96e', paddingBottom: 24, marginBottom: 32 }}>
          <div>
            <img src="/travelscape-logo-full.png" alt="TLS" style={{ height: 83, display: 'block', marginBottom: -14, marginLeft: -9 }} />
            <img src="/iata-logo.svg" alt="IATA" style={{ width: 92, height: 'auto', display: 'block', marginBottom: 2 }} />
            <div style={{ fontSize: 11, color: '#666', lineHeight: 1.6 }}>
              Travelscape Lanka Pvt Ltd<br />
              Colombo, Sri Lanka
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a', letterSpacing: 1 }}>QUOTATION</div>
            <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>{quot.quotation_number}</div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>Date: {today}</div>
            {quot.valid_until && (
              <div style={{ fontSize: 12, color: '#c0392b', marginTop: 2, fontWeight: 600 }}>
                Valid Until: {new Date(quot.valid_until).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
              </div>
            )}
            {(quot.advance_percent != null || quot.advance_amount != null) && (
              <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                Advance Required: {quot.advance_percent != null
                  ? `${quot.advance_percent}%${quot.advance_amount != null ? ` (${quot.currency_code || 'LKR'} ${parseFloat(quot.advance_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })})` : ''}`
                  : `${quot.currency_code || 'LKR'} ${parseFloat(quot.advance_amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
              </div>
            )}
          </div>
        </div>

        {/* Package title + meta */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: '#1a1a1a', marginBottom: 8 }}>{quot.title}</div>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            {quot.destination && <div style={{ fontSize: 13, color: '#555' }}><span style={{ fontWeight: 600, color: '#333' }}>Destination:</span> {quot.destination}</div>}
            {quot.duration && <div style={{ fontSize: 13, color: '#555' }}><span style={{ fontWeight: 600, color: '#333' }}>Duration:</span> {quot.duration}</div>}
          </div>
        </div>

        {/* DETAIL PACKAGE: one full block per option */}
        {isDetail && options.map((opt, optIdx) => (
          <div key={optIdx} className={options.length > 1 ? '' : undefined} style={{ marginBottom: 36, paddingBottom: optIdx < options.length - 1 ? 24 : 0, borderBottom: optIdx < options.length - 1 ? '2px dashed #ddd' : 'none' }}>
            {options.length > 1 && (
              <div className="avoid-break" style={{ fontSize: 16, fontWeight: 800, color: '#c8a96e', marginBottom: 14, textTransform: 'uppercase', letterSpacing: 1 }}>
                {opt.label || `Option ${optIdx + 1}`}
              </div>
            )}

            {(opt.travel_period || opt.book_before) && (
              <div className="avoid-break" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
                {opt.travel_period && <div style={{ fontSize: 13, color: '#555' }}><span style={{ fontWeight: 600, color: '#333' }}>Travel Period:</span> {opt.travel_period}</div>}
                {opt.book_before && <div style={{ fontSize: 13, color: '#c0392b', fontWeight: 600 }}>Book Before: {opt.book_before}</div>}
              </div>
            )}

            {opt.pax_pricing?.some(p => p.rate) && (
              <div className="avoid-break" style={{ background: '#f8f4ee', border: '2px solid #c8a96e', borderRadius: 8, padding: '20px 28px', marginBottom: 28 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888', marginBottom: 14 }}>Package Pricing</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '10px 48px' }}>
                  {opt.pax_pricing.filter(p => p.type && p.rate).map((p, i) => (
                    <React.Fragment key={i}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#333' }}>{p.type}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: '#1a1a1a', textAlign: 'right' }}>
                        {quot.currency_code} {parseFloat(p.rate).toLocaleString()}
                        <span style={{ fontSize: 11, fontWeight: 500, color: '#888', marginLeft: 6 }}>per person</span>
                      </div>
                    </React.Fragment>
                  ))}
                </div>
                {opt.min_pax_note && (
                  <div style={{ marginTop: 14, fontSize: 12, color: '#888', fontStyle: 'italic', borderTop: '1px solid #e0d4be', paddingTop: 10 }}>
                    {opt.min_pax_note}
                  </div>
                )}
              </div>
            )}

            {opt.inclusions?.length > 0 && (
              <div className="avoid-break" style={{ marginBottom: 28 }}>
                <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888', marginBottom: 12 }}>Package Inclusions</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' }}>
                  {opt.inclusions.map((inc, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 15, fontWeight: 700, color: '#333', lineHeight: 1.5 }}>
                      <span style={{ color: '#c8a96e', fontWeight: 700, marginTop: 1 }}>✓</span>
                      <span>{inc}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {opt.exclusions?.length > 0 && (
              <div className="avoid-break" style={{ marginBottom: 28 }}>
                <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888', marginBottom: 12 }}>Package Exclusions</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' }}>
                  {opt.exclusions.map((exc, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 15, fontWeight: 700, color: '#888', lineHeight: 1.5 }}>
                      <span style={{ color: '#999', fontWeight: 700, marginTop: 1 }}>✕</span>
                      <span>{exc}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {opt.flights?.length > 0 && opt.flights.some(f => f.airline || f.flight_no) && (
              <div className="avoid-break" style={{ marginBottom: 28 }}>
                <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888', marginBottom: 12 }}>Flight Details</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#f8f4ee', borderBottom: '2px solid #c8a96e' }}>
                      {['Airline', 'Flight No', 'Date', 'Route', 'Departure', 'Arrival'].map(h => (
                        <th key={h} style={{ textAlign: 'left', padding: '8px 10px', fontWeight: 700, color: '#555', fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {opt.flights.filter(f => f.airline || f.flight_no).map((fl, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '8px 10px', fontWeight: 600 }}>{fl.airline}</td>
                        <td style={{ padding: '8px 10px' }}>{fl.flight_no}</td>
                        <td style={{ padding: '8px 10px' }}>{fl.date}</td>
                        <td style={{ padding: '8px 10px' }}>{fl.from} → {fl.to}</td>
                        <td style={{ padding: '8px 10px' }}>{fl.dep}</td>
                        <td style={{ padding: '8px 10px' }}>{fl.arr}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {opt.itinerary?.length > 0 && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888', marginBottom: 12 }}>Trip Overview</div>
                {opt.itinerary.map((day, i) => (
                  <div key={i} className="avoid-break" style={{ display: 'flex', gap: 12, marginBottom: 6, fontSize: 13, lineHeight: 1.5 }}>
                    <div style={{ fontWeight: 700, color: '#c8a96e', minWidth: 48, flexShrink: 0 }}>Day {day.day}</div>
                    <div style={{ color: '#333' }}>
                      {day.title}
                      {day.activities?.length > 0 && (
                        <span style={{ color: '#888' }}> — {day.activities.join(' · ')}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {opt.itinerary?.length > 0 && (
              <div style={{ marginBottom: 28 }}>
                <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888', marginBottom: 16 }}>Tour Itinerary</div>
                {opt.itinerary.map((day, i) => (
                  <div key={i} className="avoid-break" style={{ marginBottom: 20 }}>
                    <div style={{ background: '#f8f4ee', borderLeft: '4px solid #c8a96e', padding: '8px 14px', marginBottom: 8 }}>
                      <span style={{ fontWeight: 700, color: '#8b6914', fontSize: 13 }}>Day {day.day}: </span>
                      <span style={{ fontWeight: 600, color: '#1a1a1a', fontSize: 13 }}>{day.title}</span>
                    </div>
                    {day.activities?.length > 0 && (
                      <div style={{ paddingLeft: 18 }}>
                        {day.activities.map((act, j) => (
                          <div key={j} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 12, color: '#444', lineHeight: 1.5 }}>
                            <span style={{ color: '#c8a96e', marginTop: 1 }}>•</span>
                            <span>{act}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* SIMPLE: single rate box */}
        {!isDetail && quot.per_pax_rate && (
          <div className="avoid-break" style={{ background: '#f8f4ee', border: '1px solid #c8a96e', borderRadius: 8, padding: '20px 28px', marginBottom: 28, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888', marginBottom: 4 }}>Rate Per Person</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#1a1a1a' }}>
                {quot.currency_code} {parseFloat(quot.per_pax_rate).toLocaleString()}
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Per person, based on twin sharing</div>
            </div>
            {(quot.min_pax || quot.max_pax) && (
              <div style={{ textAlign: 'right', fontSize: 13, color: '#555' }}>
                <div style={{ fontWeight: 600 }}>Group Size</div>
                <div>{quot.min_pax && quot.max_pax ? `${quot.min_pax} – ${quot.max_pax} pax` : quot.min_pax ? `Min ${quot.min_pax} pax` : `Max ${quot.max_pax} pax`}</div>
              </div>
            )}
          </div>
        )}

        {/* Inclusions — Simple mode only; Detail Package shows these per-option above */}
        {!isDetail && quot.inclusions?.length > 0 && (
          <div className="avoid-break" style={{ marginBottom: 28 }}>
            <div style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888', marginBottom: 12 }}>Package Inclusions</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 24px' }}>
              {quot.inclusions.map((inc, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 15, fontWeight: 700, color: '#333', lineHeight: 1.5 }}>
                  <span style={{ color: '#c8a96e', fontWeight: 700, marginTop: 1 }}>✓</span>
                  <span>{inc}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes — long free text; PagedJS paginates this naturally line-by-line,
            no unbreakable-block wrapping needed. */}
        {quot.notes && (
          <div style={{ marginBottom: 28 }}>
            <div className="avoid-break" style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888', marginBottom: 10 }}>Terms & Notes</div>
            <div style={{ fontSize: 12, color: '#555', lineHeight: 1.8, whiteSpace: 'pre-line' }}>{quot.notes}</div>
          </div>
        )}

        {/* Terms & Conditions — same reasoning as Notes above */}
        {terms && (
          <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #e5e7eb' }}>
            <div className="avoid-break" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#888', marginBottom: 8 }}>Terms & Conditions</div>
            <div style={{ fontSize: 11, color: '#555', lineHeight: 1.8, whiteSpace: 'pre-line' }}>{terms}</div>
          </div>
        )}

        {/* Body closing note */}
        <div className="avoid-break" style={{ borderTop: '1px solid #e5e7eb', paddingTop: 20, marginTop: 16, marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
          <div style={{ fontSize: 11, color: '#999' }}>
            This quotation is prepared for general guidance only and does not constitute a booking.<br />
            To confirm your tour, a Pro Forma Invoice will be issued upon request.
          </div>
          <div style={{ textAlign: 'right', fontSize: 11, color: '#999' }}>
            <div style={{ borderTop: '1px solid #ccc', paddingTop: 8, marginTop: 32, minWidth: 160 }}>Authorised Signature</div>
          </div>
        </div>
      </PagedPrintView>
    </>
  );
}
